import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Iconify from '../../components/Iconify';
import { AvailableDeliverySlots } from '../../client/services';

type SlotUsage = {
  slot: string;
  maxPoints: number;
  usedPoints: number;
  remainingPoints: number;
};

type DaySummary = {
  date: string;
  day: number;
  usedPoints: number;
  remainingPoints: number;
  maxPoints: number;
  slots: SlotUsage[];
  exception?: boolean;
};

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

type CapacityTone = 'available' | 'filling' | 'almostFull';

const TONE_STYLES: Record<
  CapacityTone,
  {
    cardBg: string;
    border: string;
    bar: string;
    barTrack: string;
    pct: string;
    leftBg: string;
  }
> = {
  available: {
    cardBg: '#F3FBF4',
    border: 'rgba(76, 175, 80, 0.35)',
    bar: '#43A047',
    barTrack: 'rgba(67, 160, 71, 0.18)',
    pct: '#2E7D32',
    leftBg: 'rgba(67, 160, 71, 0.1)',
  },
  filling: {
    cardBg: '#FFF8F0',
    border: 'rgba(251, 140, 0, 0.4)',
    bar: '#FB8C00',
    barTrack: 'rgba(251, 140, 0, 0.18)',
    pct: '#EF6C00',
    leftBg: 'rgba(251, 140, 0, 0.1)',
  },
  almostFull: {
    cardBg: '#FFF5F5',
    border: 'rgba(229, 57, 53, 0.4)',
    bar: '#E53935',
    barTrack: 'rgba(229, 57, 53, 0.16)',
    pct: '#C62828',
    leftBg: 'rgba(229, 57, 53, 0.1)',
  },
};

const formatDateForApi = (year: number, month: number, day: number): string => {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
};

const formatDateForModal = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  const date = new Date(year, month - 1, day);
  const dayNum = date.getDate();
  const monthLabel = date.toLocaleString('en-GB', { month: 'short' });
  return `${dayNum} ${monthLabel} ${date.getFullYear()}`;
};

const getDaysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

const getBookedPercent = (usedPoints: number, maxPoints: number): number => {
  if (!maxPoints || maxPoints <= 0) return 0;
  return Math.min(100, Math.max(0, (usedPoints / maxPoints) * 100));
};

/** Booked fill: <50% available, 50–85% filling, >85% almost full */
const getCapacityTone = (
  usedPoints: number,
  remainingPoints: number,
  maxPoints: number
): CapacityTone => {
  if (remainingPoints <= 0 && maxPoints > 0) return 'almostFull';
  const bookedPct = getBookedPercent(usedPoints, maxPoints);
  if (bookedPct > 85) return 'almostFull';
  if (bookedPct >= 50) return 'filling';
  return 'available';
};

interface Props {
  refreshKey?: number;
  onEditSlotsClick?: () => void;
  embedded?: boolean;
  headerTitle?: string;
}

const DeliverySlotsCalendar = ({
  refreshKey = 0,
  onEditSlotsClick,
  embedded = false,
  headerTitle,
}: Props) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [days, setDays] = useState<DaySummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState<DaySummary | null>(null);
  const [yearAnchorEl, setYearAnchorEl] = useState<null | HTMLElement>(null);

  const firstWeekday = useMemo(
    () => new Date(year, month - 1, 1).getDay(),
    [year, month]
  );

  const loadMonth = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSelectedDay(null);

    try {
      const totalDays = getDaysInMonth(year, month);
      const dateList = Array.from({ length: totalDays }, (_, index) => {
        const day = index + 1;
        return {
          day,
          date: formatDateForApi(year, month, day),
        };
      });

      const responses = await Promise.all(
        dateList.map((item) =>
          AvailableDeliverySlots.find({
            query: {
              date: item.date,
              points: 0,
              acceptance: false,
            },
          })
        )
      );

      setDays(
        dateList.map((item, index) => {
          const slots = (responses[index]?.slots || []).map((slot: any) => ({
            slot: String(slot.slot || ''),
            maxPoints: Number(slot.maxPoints) || 0,
            usedPoints: Number(slot.usedPoints) || 0,
            remainingPoints: Number(slot.remainingPoints) || 0,
          }));

          const usedPoints = slots.reduce(
            (sum: number, slot: SlotUsage) => sum + slot.usedPoints,
            0
          );
          const maxPoints = slots.reduce(
            (sum: number, slot: SlotUsage) => sum + slot.maxPoints,
            0
          );

          return {
            date: item.date,
            day: item.day,
            usedPoints,
            remainingPoints: Math.max(maxPoints - usedPoints, 0),
            maxPoints,
            slots,
            exception: Boolean(responses[index]?.exception),
          };
        })
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load calendar points');
      setDays([]);
    } finally {
      setIsLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth, refreshKey]);

  const dayByNumber = useMemo(() => {
    const map = new Map<number, DaySummary>();
    days.forEach((day) => map.set(day.day, day));
    return map;
  }, [days]);

  const cells = useMemo(() => {
    const totalDays = getDaysInMonth(year, month);
    const blanks = Array.from({ length: firstWeekday }, () => null);
    const dates = Array.from({ length: totalDays }, (_, index) => index + 1);
    return [...blanks, ...dates];
  }, [firstWeekday, month, year]);

  const monthStats = useMemo(() => {
    const totalCapacity = days.reduce((sum, day) => sum + day.maxPoints, 0);
    const totalBooked = days.reduce((sum, day) => sum + day.usedPoints, 0);
    const remaining = Math.max(totalCapacity - totalBooked, 0);
    const monthFill = getBookedPercent(totalBooked, totalCapacity);
    return { totalCapacity, totalBooked, remaining, monthFill };
  }, [days]);

  const shiftMonth = (delta: number) => {
    const next = new Date(year, month - 1 + delta, 1);
    setMonth(next.getMonth() + 1);
    setYear(next.getFullYear());
  };

  const title = headerTitle || 'Capacity Calendar';

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: embedded ? 1.5 : 2,
          pr: embedded ? 4.5 : { xs: 0, md: 5 }, // clearance for absolute Close button
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: embedded
                ? { xs: '1.05rem', sm: '1.2rem' }
                : { xs: '1.15rem', sm: '1.35rem' },
              color: '#1F2937',
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.3,
              fontSize: embedded ? '0.78rem' : '0.85rem',
              color: '#9CA3AF',
            }}
          >
            Daily delivery / pickup slot availability
          </Typography>
        </Box>

        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          justifyContent='center'
          flexWrap='nowrap'
        >
          {[
            {
              label: 'TOTAL CAPACITY',
              value: monthStats.totalCapacity.toLocaleString(),
              valueColor: '#1E293B',
            },
            {
              label: 'TOTAL BOOKED',
              value: monthStats.totalBooked.toLocaleString(),
              valueColor: '#CA7A1C',
            },
            {
              label: 'REMAINING',
              value: monthStats.remaining.toLocaleString(),
              valueColor: '#10B981',
            },
            {
              label: 'MONTH FILL',
              value: `${Math.round(monthStats.monthFill)}%`,
              valueColor: '#10B981',
            },
          ].map((stat) => (
            <Box
              key={stat.label}
              sx={{
                px: 1.5,
                py: 0.9,
                borderRadius: 2,
                bgcolor: '#F4F0EB',
                border: 'none',
                minWidth: 108,
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#5E6D82',
                  letterSpacing: 0.4,
                  lineHeight: 1.2,
                  mb: 0.35,
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: stat.valueColor,
                  lineHeight: 1.15,
                }}
              >
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        spacing={1}
        sx={{ mb: embedded ? 1.25 : 1.5 }}
      >
        <IconButton
          size='small'
          onClick={() => shiftMonth(-1)}
          aria-label='Previous month'
        >
          <ChevronLeftIcon />
        </IconButton>
        <Box
          onClick={(e) => setYearAnchorEl(e.currentTarget)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
            minWidth: 140,
            justifyContent: 'center',
            userSelect: 'none',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              color: '#2F2A27',
              mr: 0.25,
            }}
          >
            {MONTHS[month - 1]} {year}
          </Typography>
          {Boolean(yearAnchorEl) ? (
            <ArrowDropUpIcon sx={{ color: '#2F2A27', fontSize: '1.25rem', mt: 0.2 }} />
          ) : (
            <ArrowDropDownIcon sx={{ color: '#2F2A27', fontSize: '1.25rem', mt: 0.2 }} />
          )}
        </Box>
        <IconButton
          size='small'
          onClick={() => shiftMonth(1)}
          aria-label='Next month'
        >
          <ChevronRightIcon />
        </IconButton>

        {onEditSlotsClick ? (
          <Button
            size='small'
            variant='outlined'
            startIcon={<Iconify icon={'eva:plus-fill'} />}
            onClick={onEditSlotsClick}
            sx={{ ml: 1, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Edit slots
          </Button>
        ) : null}
      </Stack>

      <Popover
        open={Boolean(yearAnchorEl)}
        anchorEl={yearAnchorEl}
        onClose={() => setYearAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 280,
            borderRadius: '12px',
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            mt: 0.75,
          },
        }}
      >
        {/* Header inside popover */}
        <Box
          onClick={() => setYearAnchorEl(null)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            mb: 1.5,
            px: 0.5,
            userSelect: 'none',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              color: '#2F2A27',
              mr: 0.25,
            }}
          >
            {MONTHS[month - 1]} {year}
          </Typography>
          <ArrowDropUpIcon sx={{ color: '#2F2A27', fontSize: '1.25rem', mt: 0.2 }} />
        </Box>

        {/* Scrollable Years Grid */}
        <Box
          sx={{
            maxHeight: 240,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1.25,
            py: 0.5,
            pr: 0.5,
            /* Custom scrollbar to look sleek */
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              borderRadius: '3px',
            },
          }}
        >
          {Array.from({ length: 31 }, (_, i) => 2015 + i).map((y) => {
            const isActive = y === year;
            return (
              <Box
                key={y}
                onClick={() => {
                  setYear(y);
                  setYearAnchorEl(null);
                }}
                sx={{
                  py: 1,
                  textAlign: 'center',
                  borderRadius: '20px',
                  fontWeight: isActive ? 700 : 600,
                  fontSize: '0.95rem',
                  bgcolor: isActive ? '#EE3750' : 'transparent',
                  color: isActive ? '#FFFFFF' : '#2F2A27',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: isActive ? '#EE3750' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {y}
              </Box>
            );
          })}
        </Box>
      </Popover>

      <Stack
        direction='row'
        spacing={2}
        justifyContent='flex-start'
        flexWrap='wrap'
        useFlexGap
        sx={{ mb: embedded ? 1.5 : 2 }}
      >
        {[
          { tone: 'available' as const, label: 'Available (<50%)' },
          { tone: 'filling' as const, label: 'Filling Up (50–85%)' },
          { tone: 'almostFull' as const, label: 'Almost Full (>85%)' },
        ].map((item) => (
          <Box
            key={item.tone}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: TONE_STYLES[item.tone].bar,
              }}
            />
            <Typography sx={{ fontSize: '0.78rem', color: '#7A7470' }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Stack>

      {isLoading ? (
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            minHeight: { xs: 320, sm: 440, md: 500 },
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Typography color='error'>{error}</Typography>
      ) : (
        <Box
          sx={{
            width: '100%',
          }}
        >
          <Box
            sx={{
              minWidth: { xs: 560, sm: 0 },
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.75,
                mb: 0.75,
              }}
            >
              {WEEKDAYS.map((day) => (
                <Box
                  key={day}
                  sx={{
                    py: 0.9,
                    textAlign: 'center',
                    borderRadius: 999,
                    bgcolor: 'rgba(180, 145, 127, 0.12)',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: '#EE3750',
                      letterSpacing: 0.4,
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0.75,
              }}
            >
              {cells.map((dayNumber, index) => {
                const summary = dayNumber ? dayByNumber.get(dayNumber) : null;
                const tone = summary
                  ? getCapacityTone(
                      summary.usedPoints,
                      summary.remainingPoints,
                      summary.maxPoints
                    )
                  : 'available';
                const styles = TONE_STYLES[tone];
                const bookedPct = summary
                  ? Math.round(
                      getBookedPercent(summary.usedPoints, summary.maxPoints)
                    )
                  : 0;
                const isToday =
                  dayNumber != null &&
                  year === now.getFullYear() &&
                  month === now.getMonth() + 1 &&
                  dayNumber === now.getDate();
                const isSelected = selectedDay?.day === dayNumber;

                return (
                  <Box
                    key={`cell-${index}`}
                    sx={{
                      minHeight: embedded
                        ? { xs: 98, sm: 108 }
                        : { xs: 108, sm: 118 },
                      borderRadius: 2.25,
                      border: '1.5px solid',
                      borderColor: !dayNumber
                        ? 'transparent'
                        : isToday
                          ? '#EE3750'
                          : isSelected
                            ? '#3D342F'
                            : styles.border,
                      p: embedded ? { xs: 0.65, sm: 0.8 } : { xs: 0.75, sm: 0.9 },
                      bgcolor: !dayNumber ? 'transparent' : styles.cardBg,
                      cursor: summary ? 'pointer' : 'default',
                      boxShadow:
                        isToday
                          ? '0 0 0 1px #EE3750'
                          : isSelected
                            ? '0 0 0 1px #3D342F'
                            : 'none',
                      transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                      '&:hover': summary
                        ? {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(33, 43, 54, 0.1)',
                          }
                        : undefined,
                    }}
                    onClick={() => {
                      if (summary) setSelectedDay(summary);
                    }}
                  >
                    {dayNumber && summary ? (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.55,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          {isToday ? (
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: '#EE3750',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#FFFFFF',
                                fontSize: { xs: 11, md: 12 },
                                fontWeight: 700,
                                lineHeight: 1,
                                p: 0,
                                boxSizing: 'border-box',
                              }}
                            >
                              <Box component='span' sx={{ display: 'block', lineHeight: 1 }}>
                                {dayNumber}
                              </Box>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: { xs: 12, md: 13 },
                                color: '#2F2A27',
                              }}
                            >
                              {dayNumber}
                            </Typography>
                          )}
                          {summary.exception ? (
                            <Typography
                              sx={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: '#7A4E00',
                                ml: 0.5,
                              }}
                            >
                              Custom
                            </Typography>
                          ) : null}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: 10, md: 11 },
                              color: styles.pct,
                            }}
                          >
                            {bookedPct}%
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 999,
                            bgcolor: styles.barTrack,
                            overflow: 'hidden',
                            mb: 0.6,
                            border: '1px solid',
                            borderColor: 'rgba(33, 43, 54, 0.2)',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${Math.max(bookedPct, bookedPct > 0 ? 3 : 0)}%`,
                              height: '100%',
                              bgcolor: styles.bar,
                              borderRadius: 999,
                              transition: 'width 0.25s ease',
                            }}
                          />
                        </Box>

                        <Stack spacing={0.25} sx={{ flex: 1 }}>
                          {[
                            { label: 'Total', value: summary.maxPoints },
                            { label: 'Booked', value: summary.usedPoints },
                          ].map((row) => (
                            <Box
                              key={row.label}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: '#7A7470',
                                  fontWeight: 600,
                                }}
                              >
                                {row.label}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  color: '#2F2A27',
                                  fontWeight: 700,
                                }}
                              >
                                {row.value}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>

                        <Box
                          sx={{
                            mt: 0.45,
                            px: 0.65,
                            py: 0.3,
                            borderRadius: 1.25,
                            bgcolor: styles.leftBg,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: styles.pct,
                            }}
                          >
                            Left
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: styles.pct,
                            }}
                          >
                            {summary.remainingPoints}
                          </Typography>
                        </Box>
                      </Box>
                    ) : dayNumber ? (
                      isToday ? (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: '#EE3750',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFFFFF',
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1,
                            p: 0,
                            boxSizing: 'border-box',
                          }}
                        >
                          <Box component='span' sx={{ display: 'block', lineHeight: 1 }}>
                            {dayNumber}
                          </Box>
                        </Box>
                      ) : (
                        <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                          {dayNumber}
                        </Typography>
                      )
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      <Dialog
        open={Boolean(selectedDay)}
        onClose={() => setSelectedDay(null)}
        fullWidth
        maxWidth='xs'
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' },
        }}
      >
        <DialogTitle sx={{ pr: 6, fontWeight: 600, color: 'text.primary' }}>
          {selectedDay?.date ? formatDateForModal(selectedDay.date) : ''}
          <IconButton
            onClick={() => setSelectedDay(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDay ? (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'primary.lighter',
                }}
              >
                {[
                  { label: 'Total', value: selectedDay.maxPoints },
                  { label: 'Booked', value: selectedDay.usedPoints },
                  { label: 'Left', value: selectedDay.remainingPoints },
                ].map((item) => (
                  <Box key={item.label} sx={{ minWidth: 64 }}>
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.35,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        fontSize: '1.2rem',
                        lineHeight: 1.2,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {selectedDay.slots.length === 0 ? (
                <Typography color='text.secondary'>
                  No slots configured for this day.
                </Typography>
              ) : (
                selectedDay.slots.map((slot) => (
                  <Box
                    key={`${selectedDay.date}-${slot.slot}`}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        color: 'text.primary',
                        mb: 0.75,
                      }}
                    >
                      {slot.slot}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', fontSize: '0.85rem' }}
                    >
                      Total: {slot.maxPoints} · Booked: {slot.usedPoints} · Left: {slot.remainingPoints}
                    </Typography>
                  </Box>
                ))
              )}
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DeliverySlotsCalendar;
